import styled from '@emotion/styled';
import type { SportTypes } from '@racemap/utilities/consts/eventTypes';
import { getShadowtrack } from '@racemap/utilities/functions/map';
import { isGroupEvent } from '@racemap/utilities/functions/utils';
import type { EventLocation, RacemapEvent } from '@racemap/utilities/types/events';
import type { PathInto, TypeOfPath } from '@racemap/utilities/types/utils';
import { Button, Collapse, Form, Input, Space, Tag } from 'antd';
import type { Immutable } from 'immer';
import { DateTime } from 'luxon';
import { type FC, useEffect, useState } from 'react';
import { useCurrentEvent } from 'src/lib/customHooks/useCurrentEvent';
import { useIsAdmin } from 'src/lib/customHooks/useIsAdmin';
import { useIsChildAccount } from 'src/lib/customHooks/useIsChildAccount';
import { getter } from '../../../../lib/FormUtils';
import { keySplitter } from '../../../../lib/FormUtils';
import type { CurrentEvent } from '../../../../store/events/events_reducers';
import { useStore } from '../../../../store/reducers';
import { BasicPanel } from '../../../BasicComponents/BasicPanel/BasicPanel';
import { DateRangePicker } from '../../../BasicComponents/DateRangePicker';
import { HStack } from '../../../BasicComponents/MetaComponent';
import SportChooser from '../../../SportChooser';
import { PayLaterList } from '../../PaymentModal/PayLaterList';

interface FormValues {
  name: string;
  location: string;
  eventType: SportTypes | null;
  eventTimes: [DateTime | undefined, DateTime | undefined];
}
type AffectedFields = 'name' | 'location' | 'startTime' | 'endTime' | 'eventType';
type UpdateEventEntry<P extends PathInto<RacemapEvent>> = {
  key: P;
  newValue: TypeOfPath<RacemapEvent, P>;
};

const extractFormFieldChanges = (
  formValues: FormValues,
  event: Immutable<CurrentEvent>,
): Array<UpdateEventEntry<AffectedFields>> => {
  const changes: Array<UpdateEventEntry<AffectedFields>> = [];

  for (const key of Object.keys(formValues)) {
    const value = formValues[key as keyof FormValues];
    if (Array.isArray(value)) {
      const [startTime, endTime] = value;
      const currentStartTime = DateTime.fromISO(getter<string>(['startTime'], event));
      const currentEndTime = DateTime.fromISO(getter<string>(['endTime'], event));

      if (startTime != null && +startTime !== +currentStartTime) {
        changes.push({ key: 'startTime', newValue: startTime.toISO() });
      }
      if (endTime != null && +endTime !== +currentEndTime) {
        changes.push({ key: 'endTime', newValue: endTime.toISO() });
      }
      continue;
    }

    const currentValue = getter(keySplitter(key), event);
    if (currentValue !== value) {
      changes.push({ key: key as AffectedFields, newValue: value });
    }
  }

  return changes;
};

type GeoLocationDraft = { city: string; region: string; country: string; continent: string };

const toDraft = (geoLocation: EventLocation | null): GeoLocationDraft => ({
  city: geoLocation?.city ?? '',
  region: geoLocation?.region ?? '',
  country: geoLocation?.country ?? '',
  continent: geoLocation?.continent ?? '',
});

// Displays the geoLocation that gets auto-derived from the event's shadowtrack start point
// (see the Event pre-save hook). Admins may override it directly; everyone else sees it read-only.
const GeoLocationField: FC = () => {
  const event = useCurrentEvent();
  const isAdmin = useIsAdmin();
  const { updateEvent } = useStore((s) => s.events.actions);
  const [draft, setDraft] = useState<GeoLocationDraft>(toDraft(event?.geoLocation ?? null));

  useEffect(() => {
    setDraft(toDraft(event?.geoLocation ?? null));
  }, [event?.geoLocation]);

  if (event == null || event.geoLocation == null) {
    return <Input disabled placeholder="Derived automatically once a track is set" />;
  }

  if (!isAdmin) {
    const summary = [
      event.geoLocation.city,
      event.geoLocation.region,
      event.geoLocation.country,
      event.geoLocation.continent,
    ]
      .filter(Boolean)
      .join(', ');
    return <Input disabled value={summary || event.geoLocation.placeName} />;
  }

  const save = (field: keyof GeoLocationDraft, value: string) => {
    updateEvent(event.id, [{ key: `geoLocation.${field}`, newValue: value || null }]);
    if (!event.geoLocationIsManual) {
      updateEvent(event.id, [{ key: 'geoLocationIsManual', newValue: true }]);
    }
  };

  return (
    <FormRow>
      <Form.Item label="City">
        <Input
          value={draft.city}
          onChange={(e) => setDraft((d) => ({ ...d, city: e.target.value }))}
          onBlur={() => save('city', draft.city)}
        />
      </Form.Item>
      <Form.Item label="Region">
        <Input
          value={draft.region}
          onChange={(e) => setDraft((d) => ({ ...d, region: e.target.value }))}
          onBlur={() => save('region', draft.region)}
        />
      </Form.Item>
      <Form.Item label="Country">
        <Input
          value={draft.country}
          onChange={(e) => setDraft((d) => ({ ...d, country: e.target.value }))}
          onBlur={() => save('country', draft.country)}
        />
      </Form.Item>
      <Form.Item label="Continent">
        <Input
          value={draft.continent}
          onChange={(e) => setDraft((d) => ({ ...d, continent: e.target.value }))}
          onBlur={() => save('continent', draft.continent)}
        />
      </Form.Item>
      {event.geoLocationIsManual && (
        <Form.Item label=" ">
          <Button
            onClick={() => updateEvent(event.id, [{ key: 'geoLocationIsManual', newValue: false }])}
          >
            Reset to automatic
          </Button>
        </Form.Item>
      )}
    </FormRow>
  );
};

export const MetaDataPanel: FC = () => {
  const event = useCurrentEvent();
  const isAdmin = useIsAdmin();
  const [form] = Form.useForm<FormValues>();
  const { updateEvent } = useStore((s) => s.events.actions);
  const isGroup = isGroupEvent(event);
  const isChildAccount = useIsChildAccount();

  useEffect(() => {
    if (event != null) {
      form.setFieldsValue({
        name: event.name,
        location: event.location,
        eventType: event.eventType,
        eventTimes: [
          event.startTime ? DateTime.fromISO(event.startTime) : undefined,
          event.endTime ? DateTime.fromISO(event.endTime) : undefined,
        ],
      });
    }
  }, [event]);

  if (event == null) return <></>;

  const shadowtrack = getShadowtrack(event);

  const saveChangedFields = () => {
    const fieldValues = form.getFieldsValue();
    const changes = extractFormFieldChanges(fieldValues, event);

    if (changes.length === 0) return;
    updateEvent(event.id, changes);
  };

  const validateMessages = {
    required: '${label} is required!',
  };
  const requiredRule = { required: true, warningOnly: true };

  return (
    <BasicPanel forceOpen title="Provide event information">
      <Form
        form={form}
        onBlur={saveChangedFields}
        layout="vertical"
        validateMessages={validateMessages}
      >
        <FormRow>
          <Form.Item name="name" label="Name" required rules={[requiredRule]}>
            <Input placeholder="e.g. Berlin Marathon" data-1p-ignore />
          </Form.Item>
          <Form.Item label="Location" required name="location" rules={[requiredRule]}>
            <Input placeholder="e.g. Berlin, Germany" name="location" />
          </Form.Item>
          <Form.Item label="Sport" required name="eventType">
            <SportChooser withSplitTypes={false} withSymbol />
          </Form.Item>
          <Form.Item
            label="Eventtimes"
            required
            name="eventTimes"
            rules={[{ ...requiredRule, type: 'array' as const }]}
          >
            <DateRangePicker
              showTime
              allowClear={false}
              showUTCTimestamp
              keepDuration
              geoPosition={shadowtrack?.geometry.coordinates[0]}
              disabled={isGroup}
              onChange={saveChangedFields}
            />
          </Form.Item>
        </FormRow>
        {isAdmin && (
          <Collapse
            ghost
            items={[
              {
                key: 'geoLocation',
                label: (
                  <Space>
                    Derived Geo Location {event.geoLocationIsManual ? <Tag>Manual</Tag> : null}
                  </Space>
                ),
                children: <GeoLocationField />,
              },
            ]}
          />
        )}
      </Form>
      <PayLaterList
        event={event}
        expandable={!isChildAccount}
        defaultShort
        title="Possible Costs"
      />
    </BasicPanel>
  );
};

const FormRow = styled(HStack)`
  justify-content: flex-start;
  align-items: flex-start;
  column-gap: 15px;
  flex-wrap: wrap;
  container-type: inline-size;

  .ant-form-item {
    max-width: 25%;
    flex: 1;

    @container (max-width: 1400px) {
      min-width: 40%;
      max-width: 50%;
    }  

    @container (max-width: 600px) {
      min-width: 100%;
    }
  }

  .ant-picker {
    width: 100%;
  }
`;
